/**
 * Wireline & Slickline AI drafts (WL-6, fieldiq-api#1362): names, skins, who
 * may press what, and how a refusal reads. WS-6's lib/ws-ai.ts, for this
 * vertical.
 *
 * NOTHING HERE COMPUTES A FIGURE. Hours, footage, counts, flags and states are
 * the API's deterministic layer; this file words them. The AI's part is the
 * draft text only, shown as a draft until a person approves it.
 */
import { wlApiError, type WlSkin } from './wireline-slickline';
import type { WlAiAgent } from '../types/wl-ai';

/** Mirrors fieldiq-api lib/wl-roles.ts WL_AI_ROLES. For hiding, not enforcing. */
export const WL_AI_ROLES = ['admin', 'office_staff', 'supervisor'] as const;
export const canUseWlAi = (role: string | null | undefined): boolean => !!role && (WL_AI_ROLES as readonly string[]).includes(role);

export const WL_AI_AGENT_LABEL: Record<WlAiAgent, string> = {
  wl_sheet_summary: 'Day-sheet summary',
  wl_ticket_preflight: 'Preflight',
  wl_service_report_draft: 'Service report draft',
  wl_compliance_digest: 'Compliance digest',
};
export const wlAiAgentLabel = (v: string | null | undefined): string =>
  v && Object.prototype.hasOwnProperty.call(WL_AI_AGENT_LABEL, v) ? WL_AI_AGENT_LABEL[v as WlAiAgent] : '—';

const UNKNOWN: WlSkin = { label: '—', fill: 'bg-fi-signal-slate', rail: 'border-l-transparent' };
const pick = (map: Record<string, WlSkin>, v: string | null | undefined): WlSkin =>
  v && Object.prototype.hasOwnProperty.call(map, v) ? map[v] : UNKNOWN;

/**
 * Review outcome (agent_runs.outcome). A pending draft reads as WAITING ON A
 * PERSON, never as anything published. Unknown → `—`, never a guessed state.
 */
export const WL_AI_OUTCOME_SKIN: Record<string, WlSkin> = {
  pending: { label: 'Awaiting your approval', fill: 'bg-fi-signal-amber', rail: 'border-l-fi-amber' },
  actioned: { label: 'Approved', fill: 'bg-fi-signal-green', rail: 'border-l-fi-green' },
  dismissed: { label: 'Dismissed', fill: 'bg-fi-signal-slate', rail: 'border-l-fi-steel' },
  superseded: { label: 'Superseded', fill: 'bg-fi-signal-slate', rail: 'border-l-fi-steel' },
  no_action: { label: 'No action', fill: 'bg-fi-signal-slate', rail: 'border-l-fi-steel' },
};
export const wlAiOutcomeSkin = (v: string | null | undefined): WlSkin => pick(WL_AI_OUTCOME_SKIN, v);

/**
 * The register's review filter. `approved` is the API's `actioned`. The API
 * filters `outcome` BEFORE its 20-row limit, so the chip is sent to the API
 * rather than applied to the rows it returned.
 */
export const WL_AI_OUTCOME_FILTERS = [
  { key: 'pending', label: 'Pending', outcome: 'pending' },
  { key: 'approved', label: 'Approved', outcome: 'actioned' },
  { key: 'dismissed', label: 'Dismissed', outcome: 'dismissed' },
  { key: 'superseded', label: 'Superseded', outcome: 'superseded' },
] as const;
export type WlAiOutcomeFilter = (typeof WL_AI_OUTCOME_FILTERS)[number]['key'];
export const wlAiOutcomeOf = (filter: string): string | null =>
  WL_AI_OUTCOME_FILTERS.find((f) => f.key === filter)?.outcome ?? null;

/**
 * A compliance item's state (fieldiq-api lib/wl-ai-digests.ts STATE_ORDER).
 *
 * `expiry_not_recorded` is a WARNING: the certificate is held but nobody wrote
 * down when it runs out. It is amber and says exactly that — never red, never
 * "held" or "blocked", because the API does not refuse anything over it.
 */
export const WL_EXPIRY_NOT_RECORDED_LABEL = 'Expiry date not recorded';
export const WL_AI_STATE_SKIN: Record<string, WlSkin> = {
  expired: { label: 'Expired', fill: 'bg-fi-signal-red', rail: 'border-l-fi-red' },
  recert_required: { label: 'Recertification required', fill: 'bg-fi-signal-red', rail: 'border-l-fi-red' },
  overdue: { label: 'Overdue', fill: 'bg-fi-signal-red', rail: 'border-l-fi-red' },
  failed: { label: 'Failed', fill: 'bg-fi-signal-red', rail: 'border-l-fi-red' },
  lost_in_hole: { label: 'Lost in hole', fill: 'bg-fi-signal-red', rail: 'border-l-fi-red' },
  over_level: { label: 'Over the dose level', fill: 'bg-fi-signal-red', rail: 'border-l-fi-red' },
  out_too_long: { label: 'Out too long', fill: 'bg-fi-signal-red', rail: 'border-l-fi-red' },
  missing: { label: 'Missing', fill: 'bg-fi-signal-red', rail: 'border-l-fi-red' },
  never_tested: { label: 'Never tested', fill: 'bg-fi-signal-red', rail: 'border-l-fi-red' },
  no_stack: { label: 'No pressure-control stack', fill: 'bg-fi-signal-red', rail: 'border-l-fi-red' },
  would_fail_on_job: { label: 'Fails the check for the job', fill: 'bg-fi-signal-amber', rail: 'border-l-fi-amber' },
  expiry_not_recorded: { label: WL_EXPIRY_NOT_RECORDED_LABEL, fill: 'bg-fi-signal-amber', rail: 'border-l-fi-amber' },
  due_soon: { label: 'Due soon', fill: 'bg-fi-signal-amber', rail: 'border-l-fi-amber' },
};
export const wlAiStateSkin = (v: string | null | undefined): WlSkin => pick(WL_AI_STATE_SKIN, v);

/** What a stored run covered, from its trigger data. Unknown → `—`. */
export function wlAiScopeText(agent: string, triggerData: Record<string, unknown> | null, day: (v: string | null) => string): string {
  const t = triggerData ?? {};
  const str = (k: string) => (typeof t[k] === 'string' && t[k] ? (t[k] as string) : null);
  if (agent === 'wl_sheet_summary') {
    const parts = [str('jobNumber'), str('workDate') ? day(str('workDate')) : null].filter((p): p is string => !!p && p !== '—');
    return parts.length ? `Day sheet · ${parts.join(' · ')}` : '—';
  }
  if (agent === 'wl_ticket_preflight') return str('ticketNumber') ?? (str('jobNumber') ? `${str('jobNumber')} (whole job)` : '—');
  if (agent === 'wl_service_report_draft') return str('jobNumber') ? `Report on ${str('jobNumber')}` : '—';
  if (agent === 'wl_compliance_digest') return str('today') ? `As of ${day(str('today'))}` : '—';
  return '—';
}

/**
 * The statuses a ticket preflight may be offered on. The API refuses a
 * customer-approved ticket (409 ticket_customer_approved) and a void one (409
 * ticket_void). An ALLOWLIST, so a status this file does not know is not
 * offered a run the API might refuse.
 */
export const WL_PREFLIGHTABLE_STATUSES = ['draft', 'office_approved', 'customer_rejected'] as const;
export const wlTicketPreflightable = (status: string | null | undefined): boolean =>
  !!status && (WL_PREFLIGHTABLE_STATUSES as readonly string[]).includes(status);

/** The API's edit limits on approve (routes/wireline-slickline-ai.routes.ts). Checked there; mirrored here to say so first. */
export const WL_AI_MAX_NARRATIVE = 2000;
export const WL_AI_MAX_OBSERVATIONS = 8;
export const WL_AI_MAX_OBSERVATION = 400;

export const WL_AI_PERMISSION_REFUSAL = 'You do not have permission to do this. AI drafts are for admins, office staff and supervisors.';

/** The 409 a draft for an issued report earns — on the run and on approve. Said as a sentence, never a code. */
export const WL_REPORT_ISSUED_SENTENCE =
  'This service report is already issued, so its narrative cannot change. Draft a summary only on a draft report; dismiss any draft written before it was issued.';

/**
 * A refusal from the AI routes, in the API's own words. These routes answer
 * `{ error, code }` and every `error` is already a sentence; a 403 is a
 * refusal whatever the body says; `report_issued` gets the sentence above.
 */
export function wlAiRefusal(err: unknown, fallback: string): string {
  const e = wlApiError(err);
  if (e.status === 403) return WL_AI_PERMISSION_REFUSAL;
  if (e.code === 'report_issued') return WL_REPORT_ISSUED_SENTENCE;
  if (e.status === 404 && !e.code) return 'That record no longer exists. It may have been deleted.';
  return e.message ?? fallback;
}
